import {
  Component,
  Input,
  OnChanges
} from '@angular/core';

import {
  CardVM,
  CardKind,
  RowVM,
  SmartCaseGroup
}                             from '../typings/haynes-pro.diagnostic-tool.smart-pack';
import { ImageViewerService } from '../../shared/components/image-viewer/image-viewer.service';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-smart-pack-details',
  template: require('./haynes-pro.diagnostic-tool.smart-pack-details.component.html'),
  styles: [require('./haynes-pro.diagnostic-tool.smart-pack-details.component.scss')]
})
export class HaynesProDiagnosticToolSmartpackDetailsComponent implements OnChanges {

  @Input() data      : Array<SmartCaseGroup> | any;
  @Input() group     : string;
  @Input() category  : string;
  @Input() maxHeight = '56vh';

  cards: Array<CardVM> = [];

  get groupLabel(): string {
    return this.group;
  }

  constructor(
    private imageViewerService: ImageViewerService
  ) {}

  ngOnChanges () : void {
    const groups = this.toGroupsArray(this.data);

    this.cards = groups
      .map(g => this.groupToCard(g))
      .filter(c => c.rows.length || c.title);
  }

  iconFor (kind : CardKind ) : string {
    switch (kind) {
      case 'symptom'   :
        return 'clipboard';
      case 'cause'     :
        return 'flag-o';
      case 'solution'  :
        return 'wrench';
      case 'parts'     :
        return 'asterisk';
      case 'repair'    :
        return 'clock-o';
      case 'disclaimer':
        return 'exclamation-circle';
      default          :
        return 'info-circle';
    }
  }

  toGroupsArray (input: any ) : Array<SmartCaseGroup> {
    if (!input) {
      return [];
    }
    if (Array.isArray(input)) {
      return input as SmartCaseGroup[];
    }
    if (input.groups && Array.isArray(input.groups)) {
      return input.groups as SmartCaseGroup[];
    }
    return [];
  }

  groupToCard ( g: SmartCaseGroup ) : CardVM {
    const title = (g.group_description || '').trim();
    const kind  = this.detectKind(title);

    const rows: Array<RowVM> = [];

    for ( const b of (g.bulletins || []) ) {
      const hasAny =
        (b.text && b.text.trim() !== '') ||
        (b.remark && b.remark.trim() !== '') ||
        (b.eobdcode && b.eobdcode.trim() !== '') ||
        (b.image && b.image.trim() !== '') ||
        (b.oe_code && b.oe_code.trim() !== '') ||
        (b.repair_time !== undefined);

      if (!hasAny) {
        rows.push({ kind: 'spacer' });

        continue;
      }

      // image-only or text+image
      if (b.image && !b.eobdcode && !b.oe_code && b.repair_time === undefined) {
        if (b.text) {
          rows.push({ kind: 'line', text: b.text, sub: this.cleanText(b.remark) });
        }

        rows.push({ kind: 'image', src: b.image });

        continue;
      }

      // EOBD / DTC codes
      if (b.eobdcode) {
        const codes = this.splitCodes(b.eobdcode);

        rows.push({
          kind  : 'codes',
          prefix: b.text ? b.text : undefined,
          codes,
          sub   : this.cleanText(b.remark)
        });

        continue;
      }

      // Repair time / OE lines
      if (b.oe_code || b.repair_time !== undefined) {
        rows.push({
          kind: 'time',
          text: b.text ? b.text : '',
          sub : this.cleanText(b.remark),
          oe  : b.oe_code ? b.oe_code : undefined,
          time: this.formatMinutes(b.repair_time)
        });

        continue;
      }

      // Plain text line (with optional remark)
      if (b.text || b.remark) {
        rows.push({
          kind: 'line',
          text: b.text ? b.text : '',
          sub : this.cleanText(b.remark)
        });

        continue;
      }

      // fallback spacer
      rows.push({ kind: 'spacer' });
    }

    return { title, kind, rows };
  }

  detectKind ( title: string ) : CardKind {
    const t = title.toLowerCase();

    if (t.includes('symptom')) {
      return 'symptom';
    }
    if (t.includes('cause')) {
      return 'cause';
    }
    if (t.includes('solution')) {
      return t.endsWith('s')
      ? 'solutions'
      : 'solution';
    }
    if (t.includes('repair time')) {
      return 'repair';
    }
    if (t.includes('parts')) {
      return 'parts';
    }
    if (t.includes('disclaimer')) {
      return 'disclaimer';
    }
    return 'generic';
  }

  splitCodes ( s: string ) : string[] {
    return (s || '')
      .split(/[,\s/]+/)
      .map(x => x.trim())
      .filter(Boolean);
  }

  formatMinutes ( min: string | number | undefined ) : string | undefined {
    if (min === undefined || min === null || min === '') {
      return undefined;
    }
    const n = Number(min);

    if (!isFinite(n)) {
      return undefined;
    }

    const h   = n / 60;
    const val = Math.round(h * 10) / 10;

    return `${val}h`;
  }

  cleanText(s?: string): string | undefined {
    const t = (s || '').trim();
    return t ? t : undefined;
  }

  viewImage ( src : string ) : void {
    this.imageViewerService.view(src);
  }
}